import { TransferCommonParameters, TransferEmailParameters, TransferLinkParameters } from '@app/client/uploader/interfaces/transfer';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { GetDropboxPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';
import { UploadInput, UploaderError, UploaderStatus } from '@smash-sdk/uploader';
import { CreateDropboxTransfer, TransferFiles } from '@smash-sdk/uploader/dist/types/interface/Transfer';
import { Language } from '../types/Language';
import { cleanDeep } from './clean-deep';
import { CloudfrontError } from './cloudfront';
import { getLanguageWithFallback } from './language';

export interface ErrorDetails {
    name?: string;
    type?: string;
    reason?: string;
    expected?: string;
    given?: number;
    min?: number;
    max?: number;
    primary?: string;
    secondary?: string;
}

export function parseUploaderError(error: UploaderError) {
    return {
        name: error.name,
        error: error.error,
        details: error.details,
        message: error.message,
        requestId: error.requestId,
        code: error.code,
    };
}

export function isUploaderFatalError(error: UploaderError | CloudfrontError<UploaderError>) {
    const fatalErrors = [
        'EmptyFileListError',
        'FileReaderAbortError',
        'FileReaderNotReadableError',
        'FileReaderSecurityError',
        'FileReaderUnknownError',
        'FileReaderNotFoundError',
        'InvalidParameterError',
        'UnauthorizedError',
        'UnvalidTokenError',
        'FileSystemAbortError',
        'FileSystemNotFoundError',
        'FileSystemPermissionDeniedError',
        'FileSystemUnknownError',
        'InvalidAvailabilityDurationError',
        'InvalidDeliveryError',
        'MissingReceiversError',
        'MissingSenderError',
        'UnsupportedFileSourceError',
        'UnsupportedFileTypeError',
        'UsageExceededError',
        'OwnerUsageExceededError',
        'NetworkError',
        'TransferIsInErrorError',
        'TransferNotFoundError',
        'UnknownError',
    ];
    return fatalErrors.includes(error.name);
}

export function shouldStartNewUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Canceled,
        UploaderStatus.Error,
    ].includes(status);
}

export function shouldResumeUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Paused,
        UploaderStatus.Queued,
        UploaderStatus.Resumable,
    ].includes(status);
}

export function isValidStatusToStartUpload(status: UploaderStatus) {
    return [
        UploaderStatus.Paused,
        UploaderStatus.Queued,
        UploaderStatus.Resumable,
        UploaderStatus.Canceled,
        UploaderStatus.Error,
    ].includes(status);
}

export function buildUploadInput(
    {
        domain,
        language,
        commonParameters,
        linkParameters,
        emailParameters,
        uploadFormMode,
        dropbox,
        files,
    }: {
        domain: GetRecordOutput['record'],
        language: Language,
        commonParameters: TransferCommonParameters,
        linkParameters: TransferLinkParameters,
        emailParameters: TransferEmailParameters,
        uploadFormMode: 'Email' | 'Link' | 'Slack',
        dropbox: GetDropboxPreviewOutput['dropbox'],
        files: {
            name: string;
            file: File | string;
        }[];
    }): UploadInput {
    const uploadInput: UploadInput = {
        files,
        teamId: domain.team,
        language: getLanguageWithFallback(language),
        password: commonParameters.password,
        preview: commonParameters.preview,
        notification: commonParameters.notification,
        availabilityDuration: commonParameters.availabilityDuration,
        promotion: commonParameters.promotion,
        customization: commonParameters.customization,
        title: commonParameters.title,
    };

    if (uploadFormMode === 'Email') {
        uploadInput.description = emailParameters.description;
        uploadInput.delivery = { type: 'Email', ...emailParameters.delivery };
        uploadInput.notification = {
            ...uploadInput.notification,
            link: { enabled: false },
            receiver: { enabled: true },
        };
    }

    if (uploadFormMode === 'Link') {
        uploadInput.customUrl = linkParameters.customUrl?.trim();
        uploadInput.accessTracking = linkParameters.accessTracking;
        uploadInput.delivery = { type: 'Link', ...linkParameters.delivery };
        uploadInput.notification = {
            ...uploadInput.notification,
            sender: { enabled: false },
            receiver: { enabled: false },
        };
    }

    if (dropbox?.id) {
        (uploadInput as TransferFiles & CreateDropboxTransfer).dropboxId = dropbox.id;
        uploadInput.delivery = {
            type: 'Dropbox',
            sender: {
                name: emailParameters.delivery.sender.name,
                email: emailParameters.delivery.sender.email
            },
        };
        uploadInput.notification = {
            download: commonParameters.notification.download,
            noDownload: commonParameters.notification.noDownload,
            sender: commonParameters.notification.sender,
            receiver: commonParameters.notification.receiver,
        };
        delete uploadInput.accessTracking;
        delete uploadInput.customUrl;
    }

    return cleanDeep(uploadInput) as UploadInput;
}
